import { getAccessTokenFromAppState, getDocumentIdFromAppState } from '../../utils'

const DEFAULT_TIMEOUT_MS = 60000
const DEFAULT_INTERVAL_MS = 1000

const isAvailable = (url, fetchFn = fetch) => {
  return new Promise((resolve, _reject) => {
    fetchFn(url, {
      headers: {
        Authorization: `Bearer ${getAccessTokenFromAppState()}`,
      },
    })
      .then((response) => {
        return response.status === 200
          ? Promise.resolve(response)
          : Promise.reject(new Error('Error polling merged pdf process'))
      })
      .then((response) => {
        return response.json()
      })
      .then((responseJson) => {
        resolve(responseJson.ready === true)
      })
      .catch(() => {
        resolve(false)
      })
  })
}

const doPolling = async ({ url, mergedDocumentId, endTime, intervalMs, timeoutMs, fetchFn }) => {
  if (Date.now() >= endTime) {
    throw new Error(`Timeout of ${timeoutMs} ms reached`)
  }

  const available = await isAvailable(url, fetchFn)
  if (available) {
    return mergedDocumentId
  }

  return new Promise((resolve, reject) => {
    setTimeout(() => {
      doPolling({ url, mergedDocumentId, endTime, intervalMs, timeoutMs, fetchFn })
        .then(resolve)
        .catch(reject)
    }, intervalMs)
  })
}

export function pollUntilReady(
  url,
  mergedDocumentId,
  timeoutMs = DEFAULT_TIMEOUT_MS,
  intervalMs = DEFAULT_INTERVAL_MS,
  fetchFn = fetch,
) {
  const endTime = Date.now() + timeoutMs
  return doPolling({ url, mergedDocumentId, endTime, intervalMs, timeoutMs, fetchFn })
}

// Utility function to generate merged PDF download URL
export async function generateMergedPdfDownloadUrl(options = {}) {
  const { onMergeSuccessful } = options
  const documentId = getDocumentIdFromAppState()
  const authHeaders = { Authorization: `Bearer ${getAccessTokenFromAppState()}` }

  // Step 1: Start merged PDF generation
  const response = await fetch(`/v2/documents/${documentId}/merged-documents`, {
    method: 'POST',
    headers: authHeaders,
  })

  if (!response.ok) {
    throw new Error('Error kicking off merged pdf process')
  }

  const json = await response.json()
  if (!json?.mergedDocumentId) {
    throw new Error('Error: no mergedDocumentId in response json')
  }

  // Call callback when merge is successfully initiated
  if (onMergeSuccessful) {
    onMergeSuccessful()
  }

  // Step 2: Poll until ready
  const mergedDocumentId = await pollUntilReady(
    `/v2/documents/${documentId}/merged-documents/${json.mergedDocumentId}/is_ready`,
    json.mergedDocumentId,
  )

  // Step 3: Get single use download link
  const downloadLinkConfig = {
    method: 'POST',
    headers: authHeaders,
  }

  const downloadResponse = await fetch(
    `/v2/documents/${documentId}/merged-documents/${mergedDocumentId}/single-use-download-link`,
    downloadLinkConfig,
  )

  if (!downloadResponse.ok) {
    throw new Error('Error getting single use download link for merged pdf')
  }

  const downloadJson = await downloadResponse.json()
  if (!downloadJson?.singleUseDownloadUrl) {
    throw new Error('Error: no singleUseDownloadUrl in response json')
  }

  return downloadJson.singleUseDownloadUrl
}
